import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Icon } from 'design-system-web';
import type { TemplateItem } from 'inspection-types';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';

import { Item } from './SectionCardItem';
import styles from './InspectionTemplateCustomizeSectionCard.scss';

type Props = {
  item: TemplateItem;
  removeItemDisabledTitle: string;
  isRemovedPresentation: boolean;
  onRemoveItem?: () => void;
  isRemoveLoading?: boolean;
  onRestoreItem?: () => void;
  isRestoreLoading?: boolean;
  /** When false, no drag handle is rendered and the underlying Item renders normally. */
  reorderEnabled: boolean;
};

export function SortableSectionCardItem({
  item,
  reorderEnabled,
  ...itemProps
}: Props) {
  const { t } = useTranslation();
  const disabled = !reorderEnabled;

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled });

  const style: React.CSSProperties = {
    transform: disabled ? undefined : CSS.Transform.toString(transform),
    transition,
  };

  const dragHandle = reorderEnabled ? (
    <button
      type="button"
      ref={setActivatorNodeRef}
      className={classNames(styles.dragHandle)}
      aria-label={t(
        'web.admin.inspections.templateCustomize.dragHandleAriaLabel'
      )}
      {...attributes}
      {...listeners}
    >
      <Icon name="grip-vertical" set={ICON_SET_FONTAWESOME} type="far" />
    </button>
  ) : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames(styles.sortableItemRow, {
        [styles.sortableItemRowDragging]: isDragging,
      })}
    >
      {dragHandle}
      <div className={styles.sortableItemContent}>
        <Item item={item} {...itemProps} />
      </div>
    </div>
  );
}
